import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Typography, Box, Tooltip, CardContent, CardMedia, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { hideModal } from 'state/actions';
import { images } from 'assets/images';
import { EmptyList } from 'pages/shared';
import { LOADING, IDLES } from 'state/constants';
import { MODAL, ADD_NEW_FAVORITE_LISTING_STEP, SERVICE_OFFERS_PUBLIC_PAGE } from 'config/constants';
import { useAuth, usePortalData, useLanguage } from 'hooks';
import { HvModal, HvButton, HvStepperDots, HvPleaseWait } from 'components';
import { BrokerFavoriteListing } from 'models';
import { isMobile } from 'utils/screen';
import ListingCard from './ListingCard';

const useStyles = makeStyles({
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    width: 900,
    '@media (max-width: 839px)': {
      width: 720,
    },
    '@media (max-width: 768px)': {
      width: 700,
    },
    '@media (max-width: 560px)': {
      width: 500,
    },
    '@media (max-width: 414px)': {
      width: 330,
    },
    '@media (max-width: 380px)': {
      width: 300,
    },
    '@media (max-width: 300px)': {
      width: 280,
    },
  },
  cancelButton: {
    backgroundColor: 'white',
    marginRight: 20,
    color: '#00bdf2',
    border: '1px solid #00bdf2',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 20,
  },
  filtersRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 10,
  },
  demoListingsContent: {
    maxHeight: '100%',
    overflowY: 'auto',
    width: '100%',
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 300px)',
    justifyContent: 'space-between',
    flexGrow: 1,
    marginTop: 10,
    '@media (max-width: 767px)': {
      justifyContent: 'center',
    },
  },
  filterButtonIcon: {
    width: 40,
    height: 40,
  },
  selectedFilterButton: {
    backgroundColor: '#00BDF2',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRight: '2px solid #F5F5F5',
    width: 60,
    cursor: 'pointer',
    height: 50,
  },
  filterButton: {
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRight: '2px solid #F5F5F5',
    width: 60,
    cursor: 'pointer',
    height: 50,
  },
  stickyDots: {
    position: 'sticky',
    bottom: 0,
  },
});
interface Props {
  setActiveStep: (_: number) => void;
  selectedListings: any;
  setSelectedListings: (_: any) => void;
  activeStep: number;
  favorite_listings: [];
}
function DemoListings({
  setActiveStep,
  activeStep,
  selectedListings,
  setSelectedListings,
  favorite_listings,
}: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const dispatch = useDispatch();
  const { user, token } = useAuth();

  const { loading, demo_listings } = usePortalData(token);
  const [filteredDemoListings, setFilteredDemoListings] = useState([]);
  const [selected_filters, setSelectedFilters] = useState({
    drone: false,
    video: false,
    floorplan: false,
    blue_hour: false,
    virtual_staging: false,
  });
  const filters = [
    { name: 'drone', image_src: images.drone_svg, image_src_selected: images.drone_white },
    {
      name: 'virtual_staging',
      image_src: images.virtualStaging,
      image_src_selected: images.virtual_staging_white,
    },
    { name: 'floorplan', image_src: images.floorplan, image_src_selected: images.floorplan_white },
    {
      name: 'blue_hour',
      image_src: images.blue_hour_svg,
      image_src_selected: images.blue_hour_white,
    },
    { name: 'video', image_src: images.video_svg, image_src_selected: images.video_white },
    {
      name: 'matterport',
      image_src: images.matterport_svg,
      image_src_selected: images.matterport_svg,
    },
  ];
  const fmtNoListings = intl.formatMessage({ id: 'portal.noListingFound' });
  useEffect(() => {
    setFilteredDemoListings(demo_listings);
  }, [demo_listings]);
  const onClose = () => {
    dispatch(hideModal(MODAL.ADD_NEW_FAVORITE_LISTING));
  };

  const onBack = () => {
    setActiveStep(ADD_NEW_FAVORITE_LISTING_STEP.OPTIONS);
  };

  const onNext = () => {
    setActiveStep(ADD_NEW_FAVORITE_LISTING_STEP.CONFIRM);
  };
  const handleClickFilter = (filter: any) => {
    const new_selected_filters = { ...selected_filters };
    new_selected_filters[filter.name] = !new_selected_filters[filter.name];
    setSelectedFilters(new_selected_filters);
    const filteredDemoListings = demo_listings.filter((demo_listing: BrokerFavoriteListing) => {
      return Object.keys(new_selected_filters).every((key) => {
        return (
          !new_selected_filters[key] ||
          demo_listing.service_offers.some((service_offer) => {
            return SERVICE_OFFERS_PUBLIC_PAGE[key].includes(service_offer.id);
          })
        );
      });
    });
    setFilteredDemoListings(filteredDemoListings);
  };
  useEffect(() => {}, [selectedListings]);
  const buttons = (
    <Box>
      <HvButton className={styles.cancelButton} onClick={onBack}>
        {intl.formatMessage({ id: 'deliver.photos.back' })}
      </HvButton>
      <HvButton onClick={onNext} disabled={!selectedListings.length}>
        {intl.formatMessage({ id: 'deliver.photos.next' })}
      </HvButton>
    </Box>
  );
  return (
    <HvModal
      title={intl.formatMessage({ id: 'portal.demoListings' })}
      onClose={onClose}
      buttons={buttons}
      largerWidth
      fullScreen={isMobile()}
    >
      <Box className={styles.modalContent}>
        <Box className={styles.filtersRow}>
          {filters.map((filter) => {
            return (
              <Tooltip
                key={filter.name}
                title={
                  <>
                    <Typography variant='subtitle1'>
                      {intl.formatMessage({ id: `portal.serviceOffer_${filter.name}` })}
                    </Typography>
                  </>
                }
              >
                <Box
                  onClick={() => {
                    return handleClickFilter(filter);
                  }}
                  className={
                    selected_filters[filter.name]
                      ? styles.selectedFilterButton
                      : styles.filterButton
                  }
                  style={filter.name === 'video' ? { borderRight: 'none' } : null}
                >
                  <img
                    className={styles.filterButtonIcon}
                    src={
                      selected_filters[filter.name] ? filter.image_src_selected : filter.image_src
                    }
                    alt='shooting'
                  />
                </Box>
              </Tooltip>
            );
          })}
        </Box>
        <Box>
          {loading === LOADING.FINISHED && filteredDemoListings.length ? (
            <Box className={styles.demoListingsContent}>
              {filteredDemoListings.map((demoListing: any) => {
                return (
                  <ListingCard
                    key={demoListing.id}
                    listingWithServiceOffer={demoListing}
                    selectedListings={selectedListings}
                    setSelectedListings={setSelectedListings}
                    setActiveStep={setActiveStep}
                    previousStep={ADD_NEW_FAVORITE_LISTING_STEP.DEMO_LISTINGS}
                    favorite_listings={favorite_listings}
                  />
                );
              })}
            </Box>
          ) : null}
        </Box>
        {loading === LOADING.STARTED ? <HvPleaseWait /> : null}
        {loading === LOADING.FINISHED && !filteredDemoListings.length ? (
          <Box className={styles.row}>
            <EmptyList text={fmtNoListings} image={images.empty_listing} />
          </Box>
        ) : null}
        <Box className={styles.stickyDots}>
          <HvStepperDots activeDot={1} numberOfDots={3} />
        </Box>
      </Box>
    </HvModal>
  );
}
export default DemoListings;
